import * as React from "react";
import { Box } from "src/components/Box";
import { BusinessInfoFilePicker } from "./BusinessInfo.filepicker";
import { FilePickerWithPreview } from "src/components/FilePicker";
import { Input } from "src/components/Input";
import { Text } from "src/components/Text";
import { TextArea } from "src/components";
import { observer } from "mobx-react-lite";
import { useBusinessInfoState } from "./BusinessInfo.state";
import { usePassportStore } from "src/stores/Passport.store";
import { useTranslation } from "src/hooks";
import { composeFiles } from "src/helpers/composeFiles";
import { errFormatter } from "src/helpers/errorFormatter";
import { eventDispatcher } from "src/helpers/eventEmitter";

import {
  BoldDividerEl,
  BoxRowEl,
  DividerEl,
  LabelEl,
  NpwpInputContainerEl,
} from "./BusinessInfo.styled";

export const BusinessInfoCorporateForm = observer(() => {
  const { outlet } = usePassportStore();
  const [uploadNpwp, setUploadNpwp] = React.useState(false);
  const translate = useTranslation();
  const state = useBusinessInfoState();
  const passportStore = usePassportStore();
  const { form, setValue, setFile } = useBusinessInfoState();
  const [isErrorUploadNpwp, setIsErrorUploadNpwp] = React.useState(false);

  const _handleOnFilePickerChange = async (file: File) => {
    try {
      setUploadNpwp(true);
      setIsErrorUploadNpwp(false);
      const fetchUrl = await passportStore.uploadFile(
        passportStore.authUser.id,
        file
      );
      const newFile = setFile(
        `midtrans.npwp_${passportStore.selectedBusinessType}`,
        file,
        fetchUrl
      );
      state.form.npwp_url = fetchUrl;
      state.setValue("files", composeFiles(state?.form?.files!, newFile));
      setUploadNpwp(false);
      eventDispatcher.publish({ action: "ob_upload_npwp_success" });
    } catch (error) {
      setIsErrorUploadNpwp(true);
      const { status, message } = errFormatter(error);
      eventDispatcher.publish({
        action: "ob_upload_npwp_error",
        payload: {
          fileType: file.type,
          message: message,
          httpStatus: status,
        },
      });
    }
  };

  return (
    <>
      <BoldDividerEl />
      <BoxRowEl>
        <Box>
          <Text fontWeight="demi">
            {translate("business_info.label.npwp_business.upload")}
          </Text>
          <Text marginTopS marginBottomL fontSize="medium">
            {translate("business_info.npwp_inline_tooltip")}
          </Text>
          <Box marginBottomL marginTopS>
            <FilePickerWithPreview
              maxSize={10}
              isLoading={
                uploadNpwp && passportStore.loadingState.has.uploadFile
              }
              onChange={_handleOnFilePickerChange}
              previewUrl={
                state.previewUrl(
                  `midtrans.npwp_${passportStore.selectedBusinessType}`
                )?.[0]
              }
              onClick={() =>
                eventDispatcher.publish({ action: "ob_upload_npwp" })
              }
              onDelete={() => {
                state.deleteFile(
                  `midtrans.npwp_${passportStore.selectedBusinessType}`
                );
                state.form.npwp_url = "";
              }}
              metadata={
                state.getFilesByName(
                  `midtrans.npwp_${passportStore.selectedBusinessType}`
                )?.[0]?.metadata
              }
              mediaType={
                state.getFilesByName(
                  `midtrans.npwp_${passportStore.selectedBusinessType}`
                )?.[0]?.media_type
              }
              onError={(error) =>
                eventDispatcher.publish({
                  action: "ob_upload_npwp_error",
                  payload: { message: error.message },
                })
              }
              isError={isErrorUploadNpwp}
            />
          </Box>
        </Box>
        <Box>
          <NpwpInputContainerEl>
            <Input
              label={translate("business_info.label.npwp_business.name")}
              placeholder={translate(
                "business_info.placeholder.npwp_business.name"
              )}
              onChange={(event: any) =>
                setValue("npwp_name", event.target.value)
              }
              defaultValue={outlet.npwpName}
              maxLength={50}
              onBlur={() => state.validateInput("npwp_name", "npwp_name")}
              error={state.inputErrors.npwp_name.isError}
              helperText={
                state.inputErrors.npwp_name.isError
                  ? translate("business_info.errors.npwp.name.required")
                  : ""
              }
            />
          </NpwpInputContainerEl>
          <NpwpInputContainerEl>
            <Input
              label={translate("business_info.label.npwp_business.number")}
              placeholder={translate(
                "business_info.placeholder.npwp_business.number"
              )}
              onChange={(event: any) => {
                setValue("npwp", event.target.value);
                state.inputErrors.npwp.isError &&
                  state.validateInput("npwp", "npwp");
              }}
              onBlur={() => state.validateInput("npwp", "npwp")}
              error={state.inputErrors.npwp.isError}
              helperText={
                state.inputErrors.npwp.isError
                  ? translate(state.inputErrors.npwp.text)
                  : ""
              }
              value={form.npwp}
              type="text"
              inputMode="numeric"
              maxLength={16}
              pattern="^[0-9]*$"
            />
          </NpwpInputContainerEl>
          <NpwpInputContainerEl>
            <LabelEl htmlFor="textarea-npwp-address">
              {translate("business_info.label.npwp_business.address")}
            </LabelEl>
            <TextArea
              placeholder={translate(
                "business_info.placeholder.npwp_business.address"
              )}
              onChange={(event: any) =>
                setValue("headquarters_address", event.target.value)
              }
              onBlur={() =>
                state.validateInput(
                  "headquarters_address",
                  "headquarters_address"
                )
              }
              error={state.inputErrors.headquarters_address.isError}
              helperText={
                state.inputErrors.headquarters_address.isError
                  ? translate("business_info.errors.npwp.address.required")
                  : ""
              }
              defaultValue={
                form.headquarters_address || outlet.headquartersAddress
              }
              maxLength={100}
            />
          </NpwpInputContainerEl>
        </Box>
      </BoxRowEl>
      <BoldDividerEl />
      <Text fontWeight="demi">{translate("business_info.label.akta")}</Text>
      <Box marginBottomL marginTopS>
        <BusinessInfoFilePicker purpose="akta" maxFiles={5} />
      </Box>
      <BoldDividerEl />
      <Text fontWeight="demi">
        {translate("business_info.label.sk_menkeh")}
      </Text>
      <Box marginBottomL marginTopS>
        <BusinessInfoFilePicker purpose="menkeh" maxFiles={5} />
      </Box>
      <DividerEl />
    </>
  );
});
